class PredictiveSearch {
  constructor(element) {
    this.predictiveSearchContainer = element;
    this.searchInput = element.querySelector('.js-predictive-search-input');
    this.formIsMobile = element.getAttribute('data-form-is-mobile') === "true";
    this.resultsContainer = null;
    this.debounceTimer = null;
    this.isLoading = false;
    this.init();
  }

  init() {
    if (!this.searchInput || !this.predictiveSearchContainer) {
      return;
    }

    this.createResultsContainer();
    this.bindEvents();
  }

  createResultsContainer() {
    // Create the results container
    this.resultsContainer = document.createElement('div');
    this.resultsContainer.id = 'predictive-search-results';
    this.resultsContainer.className = `${!this.formIsMobile ? 'absolute bg-white border border-t-0 border-gray-200 shadow-lg w-[800px] max-h-[500px]' : '' } left-0 mt-5 z-50 overflow-y-auto hidden no-scrollbar`;
    this.resultsContainer.setAttribute('data-predictive-search', 'hidden');
    this.predictiveSearchContainer.append(this.resultsContainer);
  }

  bindEvents() {
    // Input event with debouncing
    this.searchInput.addEventListener('input', (e) => {
      this.handleSearch(e.target.value);
    });

    // Focus event
    this.searchInput.addEventListener('focus', () => {
      if (this.searchInput.value.trim()) {
        this.showResults();
      }
    });

    // Blur event (with delay to allow clicking on results)
    if (window.innerWidth >= 990) {
      this.searchInput.addEventListener('blur', () => {
        setTimeout(() => {
          this.hideResults();
        }, 200);
      });
    }

    // Prevent form submission when pressing Enter on results
    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && this.resultsContainer.getAttribute('data-predictive-search') === 'visible') {
        e.preventDefault();
        const firstResult = this.resultsContainer.querySelector('a');
        if (firstResult) {
          window.location.href = firstResult.href;
        }
      }
    });
  }

  bindMobileTabEvents() {
    // Only bind events if we're on mobile (viewport width < 990px)
    if (window.innerWidth >= 990) return;

    const tabButtons = this.resultsContainer.querySelectorAll('[data-tab-target]');
    const tabContents = this.resultsContainer.querySelectorAll('[data-tab]');

    tabButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const targetTab = button.getAttribute('data-tab-target');
        
        // Update button states using data-active attribute
        tabButtons.forEach(btn => {
          btn.setAttribute('data-active', 'false');
        });
        button.setAttribute('data-active', 'true');

        // Update content visibility using data-active attribute
        tabContents.forEach(content => {
          const isTarget = content.getAttribute('data-tab') === targetTab;
          content.setAttribute('data-active', isTarget.toString());
        });
      });
    });
  }

  handleSearch(query) {
    const trimmedQuery = query.trim();
    
    // Clear previous timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Hide results if query is empty
    if (!trimmedQuery) {
      this.hideResults();
      return;
    }

    // Debounce the search
    this.debounceTimer = setTimeout(() => {
      this.performSearch(trimmedQuery);
    }, 300);
  }

  async performSearch(query) {
    if (this.isLoading) return;
    
    this.isLoading = true;
    this.showLoading();

    try {
      const response = await fetch(
        `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,query,article,page&resources[limit]=10&resources[limit_scope]=each`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.displayResults(data.resources.results, query);
    } catch (error) {
      console.error('Predictive search error:', error);
      this.showError();
    } finally {
      this.isLoading = false;
    }
  }

  displayResults(results, query) {
    if (!results || (!results.articles?.length && !results.products?.length && !results.queries?.length && !results.pages?.length)) {
      this.showNoResults(query);
      return;
    }

    let html = `<div class="${!this.formIsMobile ? 'p-3.5 md:p-5' : '' } flex flex-col gap-5 md:flex-row">`;

    // Left column: Suggestions and Pages
    html += '<div class="md:w-[190px] flex flex-col gap-y-5 md:gap-y-10">';
    
    // Search suggestions (queries) - top of left column
    if (results.queries && results.queries.length > 0) {
      html += this.renderSearchSuggestions(results.queries);
    }

    html += 
    `<div class="flex relative flex-row gap-3 flex-nowrap md:hidden after:content-[''] after:bottom-[1px] after:absolute after:left-0 after:block after:h-[1px] after:bg-[#4040401A] after:w-full">
    <button type="button" data-tab-target="pages" data-active="true" class="font-heading pb-3 border-b data-[active=true]:border-black border-transparent h6 ${!results.pages?.length && !results.articles?.length ? 'pointer-events-none opacity-50' : 'opacity-100'}">Pages</button>
      <button type="button" data-tab-target="products" data-active="false" class="font-heading pb-3 border-b data-[active=true]:border-black border-transparent h6 ${!results.products?.length ? 'pointer-events-none opacity-50' : 'opacity-100'}">Products</button>
    </div>`;

    // Pages - below suggestions in left column
    const pages = results.pages || [];
    const articles = results.articles || [];

    // Combine into a single array
    const pageResults = [...pages, ...articles].map(item => ({
      title: item.title,
      url: item.url
    }));

    if (pageResults && pageResults.length > 0) {
      html += this.renderPages(pageResults);
    }
    
    html += '</div>';

    // Right column: Products
    if (results.products && results.products.length > 0) {
      html += '<div class="flex-1 md:!block data-[active=false]:hidden" data-tab="products" data-active="false">';
      html += this.renderProducts(results.products);
      html += '</div>';
    }

    html += '</div>';

    this.resultsContainer.innerHTML = html;
    this.bindMobileTabEvents();
    this.showResults();
  }

  renderSearchSuggestions(queries) {
    let html = `
      <div>
        <p class="${this.formIsMobile ? 'mt-5' : '' } h6 mb-3 border-b border-[#4040401A] block pb-3">Search Suggestions</p>
        <ul class="list-none m-0 p-0">
    `;

    queries.forEach((query, index) => {
      // Replace highlight markup with bold tags
      const styledText = query.styled_text 
        ? query.styled_text.replace(/<mark>/g, '<strong>').replace(/<\/mark>/g, '</strong>')
        : query.text;
      
      html += `
        <li class="m-0">
          <a href="${query.url}" class="flex items-center py-2 text-gray-800 no-underline transition-colors duration-200 hover:text-black">
            <span class="text-sm">${styledText}</span>
          </a>
        </li>
      `;
    });

    html += `
        </ul>
      </div>
    `;

    return html;
  }

  renderProducts(products) {
    let html = `
      <div>
        <p class="h6 mb-3 border-b border-[#4040401A] hidden md:block pb-3">Products</p>
        <ul class="grid gap-2.5 gap-y-5 sm:grid-cols-2">
    `;

    products.forEach((product) => {
      const imageUrl = product.featured_image?.url || product.image || '';
      const price = this.formatPrice(product.price);
      const compareAtPrice = this.formatPrice(product.compare_at_price);

      html += `
        <li class="overflow-hidden">
          <a href="${product.url}" aria-label="${product.title}">
            ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" with="312" height="215" class="block w-full h-auto mb-5">` : ''}
            <p class="mb-2 capitalize line-clamp-2">${product.title}</p>
            <p class="text-sm">
              ${compareAtPrice ? `<span class="line-through mr-2 igComparePrice" data-product-id="${product.id}">$${compareAtPrice}</span>` : ''}
              <span class="igPrice" data-product-id="${product.id}">$${price}</span>
            </p>
          </a>
        </li>
      `;
    });

    html += `
        </ul>
      </div>
    `;

    return html;
  }

  renderPages(pages) {
    let html = `
      <div class="md:!block data-[active=false]:hidden" data-tab="pages" data-active="true">
        <p class="h6 mb-3 border-b border-[#4040401A] hidden md:block pb-3">Pages</p>
        <ul>
    `;

    pages.forEach((page) => {
      html += `
        <li class="m-0">
          <a href="${page.url}" class="flex items-center py-2 text-gray-800 no-underline transition-colors duration-200 hover:text-black">
            <span class="text-sm">${page.title}</span>
          </a>
        </li>
      `;
    });

    html += `
        </ul>
      </div>
    `;

    return html;
  }

  formatPrice(price) {
    if (!price) return '';
    
    // Convert to number and format with commas
    const numPrice = parseFloat(price);
    if (isNaN(numPrice)) return price;
    
    // Check if the decimal part is 0 or 00
    const hasDecimal = numPrice % 1 !== 0;
    const decimalPart = (numPrice % 1).toFixed(2).substring(2);
    
    if (!hasDecimal || decimalPart === '00') {
      // Remove decimals and add commas
      return Math.floor(numPrice).toLocaleString();
    } else {
      // Keep decimals and add commas
      return numPrice.toLocaleString();
    }
  }

  showLoading() {
    this.resultsContainer.innerHTML = `
      <div class="p-5 text-center text-gray-600">
        <p>Searching...</p>
      </div>
    `;
    this.showResults();
  }

  showNoResults(query) {
    this.resultsContainer.innerHTML = `
      <div class="p-5 text-center text-gray-600">
        <p>No results found for "${query}"</p>
      </div>
    `;
    this.showResults();
  }

  showError() {
    this.resultsContainer.innerHTML = `
      <div class="p-5 text-center text-red-500">
        <p>Search temporarily unavailable. Please try again.</p>
      </div>
    `;
    this.showResults();
  }

  showResults() {
    this.resultsContainer.classList.remove('hidden');
    this.resultsContainer.setAttribute('data-predictive-search', 'visible');
    document.body.setAttribute('data-nav-search', 'true')
    document.body.setAttribute('data-menu-active', 'true');
  }

  hideResults() {
    this.resultsContainer.classList.add('hidden');
    this.resultsContainer.setAttribute('data-predictive-search', 'hidden');
    document.body.setAttribute('data-nav-search', 'false')
    if (!this.formIsMobile) {
      document.body.setAttribute('data-menu-active', 'false');
    }
  }
}

// Initialize predictive search when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelectorAll('predictive-search')) {
    document.querySelectorAll('predictive-search').forEach(searchElement => {
      new PredictiveSearch(searchElement);
    });
  }
});